const MISSING_TEXT_VALUES = new Set([
  "",
  "blank",
  "direct_unknown",
  "n/a",
  "na",
  "none",
  "null",
  "undefined",
  "unknown",
  "unknown_city",
  "unknown_device",
  "unknown_region",
  "unknown_state",
]);

const CONTEXT_KEYS = new Set([
  "browserFamily",
  "city",
  "deviceCategory",
  "locationSource",
  "pagePath",
  "route",
  "routePath",
  "state",
  "stateCode",
  "viewportBucket",
]);

const ATTRIBUTION_KEYS = new Set([
  "adId",
  "adSetId",
  "campaign",
  "campaignId",
  "channel",
  "city",
  "acquisitionProvider",
  "acquisitionSource",
  "acquisitionConversionDisagreement",
  "acquisitionSelfReportDisagreementKind",
  "attributionBasis",
  "attributionModelVersion",
  "confidence",
  "conversionAttributionBasis",
  "conversionConfidence",
  "conversionProvider",
  "conversionSource",
  "assistedProviders",
  "clickIdTypes",
  "hasAdClickId",
  "hasFbclid",
  "hasGoogleClickId",
  "hasMetaClickId",
  "job",
  "firstPaidClickAtMs",
  "googleReferrerSeoEntryPath",
  "flyerInstanceId",
  "flyerVersion",
  "latestPaidClickAtMs",
  "paidMediaProvider",
  "medium",
  "originalRoutePath",
  "placeGeoId",
  "referralCode",
  "referralSource",
  "routePath",
  "source",
  "sourceFamily",
  "seoCohort",
  "seoExperimentKey",
  "seoVersion",
  "state",
  "taskGroup",
  "taskLabel",
  "taskSlug",
]);

const LOCATION_KEYS = new Set([
  "city",
  "locationSource",
  "state",
  "stateCode",
]);

const LOCATION_MERGE_KEYS = ["city", "state", "stateCode", "locationSource"];
const US_STATE_NAME_TO_CODE = new Map(
  [
    ["alabama", "AL"],
    ["alaska", "AK"],
    ["arizona", "AZ"],
    ["arkansas", "AR"],
    ["california", "CA"],
    ["colorado", "CO"],
    ["connecticut", "CT"],
    ["delaware", "DE"],
    ["district of columbia", "DC"],
    ["florida", "FL"],
    ["georgia", "GA"],
    ["hawaii", "HI"],
    ["idaho", "ID"],
    ["illinois", "IL"],
    ["indiana", "IN"],
    ["iowa", "IA"],
    ["kansas", "KS"],
    ["kentucky", "KY"],
    ["louisiana", "LA"],
    ["maine", "ME"],
    ["maryland", "MD"],
    ["massachusetts", "MA"],
    ["michigan", "MI"],
    ["minnesota", "MN"],
    ["mississippi", "MS"],
    ["missouri", "MO"],
    ["montana", "MT"],
    ["nebraska", "NE"],
    ["nevada", "NV"],
    ["new hampshire", "NH"],
    ["new jersey", "NJ"],
    ["new mexico", "NM"],
    ["new york", "NY"],
    ["north carolina", "NC"],
    ["north dakota", "ND"],
    ["ohio", "OH"],
    ["oklahoma", "OK"],
    ["oregon", "OR"],
    ["pennsylvania", "PA"],
    ["rhode island", "RI"],
    ["south carolina", "SC"],
    ["south dakota", "SD"],
    ["tennessee", "TN"],
    ["texas", "TX"],
    ["utah", "UT"],
    ["vermont", "VT"],
    ["virginia", "VA"],
    ["washington", "WA"],
    ["west virginia", "WV"],
    ["wisconsin", "WI"],
    ["wyoming", "WY"],
  ],
);
const US_STATE_CODES = new Set(US_STATE_NAME_TO_CODE.values());
const SOURCE_RANKS = {
  unknown: 0,
  ip: 10,
  browser: 20,
  stored: 40,
  route: 70,
  member_city_seo: 80,
  manual: 90,
};

const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_");

export const isMeaningfulFunnelSessionValue = (value, key = "") => {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") {
    return key.startsWith("has") ? value === true : true;
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;

  const normalized = normalizeText(value).toLowerCase();
  if (MISSING_TEXT_VALUES.has(normalized)) return false;
  if (normalized.startsWith("unknown_")) return false;
  return Boolean(normalized);
};

const compactMap = (value = {}, allowedKeys = null) => {
  if (!value || typeof value !== "object") return {};

  return Object.entries(value).reduce((accumulator, [key, item]) => {
    if (allowedKeys && !allowedKeys.has(key)) return accumulator;
    if (!isMeaningfulFunnelSessionValue(item, key)) return accumulator;
    accumulator[key] = item;
    return accumulator;
  }, {});
};

export const compactFunnelContext = (context = {}) =>
  compactMap(context, CONTEXT_KEYS);

export const compactFunnelAttribution = (attribution = {}) =>
  compactMap(attribution, ATTRIBUTION_KEYS);

const firstMeaningfulValue = (...values) => {
  for (const value of values) {
    if (isMeaningfulFunnelSessionValue(value)) return value;
  }
  return "";
};

const normalizeSourceToken = (value = "") =>
  normalizeText(value).toLowerCase();

const normalizeLocationSource = (value = "") => {
  const source = normalizeSourceToken(value);
  if (!source) return "unknown";
  if (source === "member_city_seo" || source === "member_seo") {
    return "member_city_seo";
  }
  if (source.includes("member_city_seo") || source.includes("member_seo")) {
    return "member_city_seo";
  }
  if (source.includes("manual") || source.includes("google")) return "manual";
  if (source.includes("route")) return "route";
  if (source.includes("stored")) return "stored";
  if (source.includes("browser")) return "browser";
  if (source.includes("ip")) return "ip";
  return "unknown";
};

const locationRank = (value = {}) => {
  const source = normalizeLocationSource(value?.locationSource || value?.source || "");
  if (source !== "unknown") return SOURCE_RANKS[source] || 0;
  if (
    normalizeSourceToken(value?.source) === "member_city_seo" ||
    normalizeSourceToken(value?.sourceFamily) === "member_seo"
  ) {
    return SOURCE_RANKS.member_city_seo;
  }
  return 0;
};

const locationCompleteness = (value = {}) =>
  LOCATION_MERGE_KEYS.reduce(
    (count, key) => count + (isMeaningfulFunnelSessionValue(value?.[key], key) ? 1 : 0),
    0,
  );

const pickLocationSource = (current = {}, next = {}) => {
  const currentLocation = compactMap(current, LOCATION_KEYS);
  const nextLocation = compactMap(next, LOCATION_KEYS);
  if (!Object.keys(currentLocation).length) return nextLocation;
  if (!Object.keys(nextLocation).length) return currentLocation;

  const currentRank = locationRank(current);
  const nextRank = locationRank(next);
  if (nextRank > currentRank) return { ...currentLocation, ...nextLocation };
  if (nextRank < currentRank) {
    return { ...nextLocation, ...currentLocation };
  }

  return locationCompleteness(nextLocation) >= locationCompleteness(currentLocation)
    ? { ...currentLocation, ...nextLocation }
    : { ...nextLocation, ...currentLocation };
};

const sourceRank = (value = {}) => {
  const firstPaidClickAtMs = Number(value?.firstPaidClickAtMs || 0);
  if (Number.isFinite(firstPaidClickAtMs) && firstPaidClickAtMs > 0) {
    return 200 - firstPaidClickAtMs / 1_000_000_000_000_000;
  }
  const latestPaidClickAtMs = Number(value?.latestPaidClickAtMs || 0);
  if (Number.isFinite(latestPaidClickAtMs) && latestPaidClickAtMs > 0) {
    return 100 + latestPaidClickAtMs / 1_000_000_000_000_000;
  }
  const source = normalizeSourceToken(value?.source);
  const family = normalizeSourceToken(value?.sourceFamily);
  if (source === "facebook" || source === "instagram" || family === "meta") {
    return 90;
  }
  if (source === "google" || family === "google") return 90;
  if (
    source === "member_city_seo" ||
    family === "member_seo" ||
    source.startsWith("hiring") ||
    family === "helper_hiring_seo"
  ) {
    return 70;
  }
  if (isMeaningfulFunnelSessionValue(source)) return 50;
  return 0;
};

const conversionRank = (value = {}) => {
  const latestPaidClickAtMs = Number(value?.latestPaidClickAtMs || 0);
  if (Number.isFinite(latestPaidClickAtMs) && latestPaidClickAtMs > 0) {
    return latestPaidClickAtMs;
  }
  return 0;
};

const mergeBestKnownMaps = (compact, items = []) =>
  items.reduce((merged, item) => {
    const next = compact(item);
    if (!Object.keys(next).length) return merged;
    return { ...merged, ...next };
  }, {});

export const normalizeFunnelStateCode = (...values) => {
  for (const value of values) {
    if (!isMeaningfulFunnelSessionValue(value, "stateCode")) continue;

    const raw = String(value || "")
      .trim()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");
    if (!raw) continue;

    const compact = raw.toUpperCase().replace(/\s+/g, "");
    if (US_STATE_CODES.has(compact)) return compact;

    const isoRegionMatch = compact.match(/^(?:US|USA)([A-Z]{2})$/);
    if (isoRegionMatch && US_STATE_CODES.has(isoRegionMatch[1])) {
      return isoRegionMatch[1];
    }

    const stateCode = US_STATE_NAME_TO_CODE.get(raw.toLowerCase());
    if (stateCode) return stateCode;
  }
  return "";
};

export const buildFunnelLocationSummary = ({
  context = {},
  properties = {},
  attribution = {},
} = {}) => {
  const location = {
    city: firstMeaningfulValue(context.city, properties.city, attribution.city),
    state: firstMeaningfulValue(context.state, properties.state, attribution.state),
    stateCode: normalizeFunnelStateCode(
      context.stateCode,
      properties.stateCode,
      attribution.stateCode,
    ),
    locationSource: firstMeaningfulValue(
      context.locationSource,
      properties.locationSource,
      attribution.locationSource,
    ),
  };

  return compactMap(location, LOCATION_KEYS);
};

export const mergeBestKnownFunnelSessionContext = (...items) => {
  const merged = mergeBestKnownMaps(compactFunnelContext, items);
  const location = items.reduce(
    (best, item) => pickLocationSource(best, item),
    {},
  );
  LOCATION_MERGE_KEYS.forEach((key) => {
    if (isMeaningfulFunnelSessionValue(location[key], key)) {
      merged[key] = location[key];
    } else {
      delete merged[key];
    }
  });
  return merged;
};

export const mergeBestKnownFunnelSessionAttribution = (...items) => {
  const merged = mergeBestKnownMaps(compactFunnelAttribution, items);
  const bestSource = items
    .map(compactFunnelAttribution)
    .filter((item) => Object.keys(item).length)
    .reduce((best, item) => {
      const bestRank = sourceRank(best);
      const nextRank = sourceRank(item);
      if (nextRank > bestRank) return item;
      if (nextRank < bestRank) return best;
      return { ...item, ...best };
    }, {});
  const bestConversion = items
    .map(compactFunnelAttribution)
    .filter((item) => Object.keys(item).length)
    .reduce((best, item) => {
      const bestRank = conversionRank(best);
      const nextRank = conversionRank(item);
      if (nextRank > bestRank) return item;
      if (nextRank < bestRank) return best;
      return { ...best, ...item };
    }, {});

  [
    "source",
    "sourceFamily",
    "acquisitionProvider",
    "acquisitionSource",
    "paidMediaProvider",
    "attributionBasis",
    "confidence",
    "firstPaidClickAtMs",
  ].forEach((key) => {
    if (isMeaningfulFunnelSessionValue(bestSource[key], key)) {
      merged[key] = bestSource[key];
    } else {
      delete merged[key];
    }
  });
  [
    "conversionProvider",
    "conversionSource",
    "conversionAttributionBasis",
    "conversionConfidence",
    "latestPaidClickAtMs",
  ].forEach((key) => {
    if (isMeaningfulFunnelSessionValue(bestConversion[key], key)) {
      merged[key] = bestConversion[key];
    }
  });
  if (items.some((item) => item?.hasFbclid === true)) {
    merged.hasFbclid = true;
  } else if (!isMeaningfulFunnelSessionValue(merged.hasFbclid, "hasFbclid")) {
    delete merged.hasFbclid;
  }
  const firstGoogleEntry = items.find((item) =>
    isMeaningfulFunnelSessionValue(item?.googleReferrerSeoEntryPath),
  );
  if (firstGoogleEntry) {
    merged.googleReferrerSeoEntryPath = firstGoogleEntry.googleReferrerSeoEntryPath;
  }
  ["hasAdClickId", "hasGoogleClickId", "hasMetaClickId"].forEach((key) => {
    if (items.some((item) => item?.[key] === true)) {
      merged[key] = true;
    } else if (!isMeaningfulFunnelSessionValue(merged[key], key)) {
      delete merged[key];
    }
  });
  ["clickIdTypes", "assistedProviders"].forEach((key) => {
    const values = Array.from(
      new Set(
        items
          .flatMap((item) => (Array.isArray(item?.[key]) ? item[key] : []))
          .filter(Boolean)
          .map((item) => String(item)),
      ),
    ).slice(0, 20);
    if (values.length) merged[key] = values;
  });
  return merged;
};
